import { FileUploadButton } from 'components/button/FileUploadButton';
import { useNotificationContext } from 'contexts/NotificationContext';
import {
    useFileUpload,
    useIsAnyFileUploading,
} from 'pages/Settings/FileLibrary/hooks/useFileUpload';
import { useRef } from 'react';
import { FileTypePatterns, normalizeFile } from 'utils/file';
import { sentryCaptureException } from 'utils/sentry';

export type MultiFileUploadProps = Readonly<{
    /**
     * upload callback
     */
    onUpload: () => void;
    /**
     * The current corporation's id
     */
    corporationId?: string;
    /**
     * The current location's id
     */
    locationId?: string;
    /**
     * The groups ids used to associate groups to the file(s)
     */
    groupIds?: string[];

    /**
     * Set the upload to be multi or single file
     */
    isMultiFile?: boolean;

    /**
     * Set the button appearance style
     */
    buttonAppearance?: 'primary' | 'secondary' | 'tertiary' | 'subtle';

    /**
     * Set the button spacing; none, normal, medium, large, undefined
     */
    buttonSpacing?: 'none' | 'medium' | 'compact' | 'large' | undefined;

    /**
     * Id to set as the ParentFolderId for the uploaded file
     */
    parentFolderId: string | undefined;
}>;

export function MultiFileUpload({
    onUpload,
    locationId,
    corporationId,
    groupIds,
    isMultiFile = true,
    buttonAppearance,
    buttonSpacing = 'medium',
    parentFolderId,
}: MultiFileUploadProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { showToast } = useNotificationContext();
    const isUploading = useIsAnyFileUploading();
    const {
        mutation: { mutate: uploadFile },
        errorMessage,
    } = useFileUpload(
        {
            locationId,
            corporationId,
            groupIds,
            parentFolderId,
            allowedFileTypes: [FileTypePatterns.image, FileTypePatterns.video],
        },
        {
            onSuccess: () => {
                onUpload();
            },
        },
    );

    const onUploadInputChange = () => {
        if (!fileInputRef.current?.files) {
            return;
        }

        for (const file of fileInputRef.current.files) {
            normalizeFile(file)
                // this fires a mutation, errors in uploadFile are handed in
                // onError
                .then(uploadFile)
                .catch((err) => {
                    sentryCaptureException({
                        error: err instanceof Error ? err : new Error(String(err)),
                        tags: { component: 'MultiFileUpload' },
                        context: {
                            file: {
                                fileName: file.name,
                                fileSize: file.size,
                                fileType: file.type,
                            },
                        },
                    });
                    showToast({
                        type: 'error',
                        content: `Failed to process file: ${file.name}. The file may be corrupted.`,
                    });
                });
        }

        // Clear the file input so that the onchange will
        // trigger if the same file is selected again
        fileInputRef.current.value = '';
    };

    return (
        <FileUploadButton
            error={errorMessage}
            isError={errorMessage !== undefined}
            fileInputRef={fileInputRef}
            isMultiFile={isMultiFile}
            buttonAppearance={buttonAppearance}
            buttonSpacing={buttonSpacing}
            onUpload={onUploadInputChange}
            onButtonClick={() => fileInputRef.current?.click()}
            isUploading={isUploading}
            buttonLabel={`Upload ${isMultiFile ? 'Files' : 'File'}`}
            fileType="imageOrVideo"
        />
    );
}
